import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import AppBar from "@material-ui/core/AppBar";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import { Grid, Button, FormControl, Select, MenuItem, CircularProgress, TextField } from "@material-ui/core";
import AddIcon from '@material-ui/icons/Add';
import GenericTable from "./GenericTable";
import PlanningTab from "./PlanningTab";
import MaterialsConsumptionTab from "./MaterialsConsumptionTab";
import InProcessTestsTab from "./InProcessTestsTab";
import ProductReleaseTestsTab from "./ProductReleaseTestsTab";
import DeviationsTab from "./DeviationsTab";
import ChangeControlTab from "./ChangeControlTab";
import CycleTimeTab from "./CycleTimeTab";
import EquipmentTab from "./EquipmentTab";
import YieldTab from "./YieldTab";
import ShipmentTab from "./ShipmentTab";
import CPPsTab from "./CPPsTab";
import DocumentsTab from "./DocumentsTab";
import CommentsTab from "./CommentsTab";
import MFGStatusTab from "./MFGStatusTab";
import MessagesDashboard from "./messages/MessagesDashboard";
import { getViewTypeParam, getViewTypeFromParam } from "./tabsAPI";
import { getTabActionsConfig } from "./actions/tabActionsConfig";
import { downloadTabExcel } from "./tabExcelExport";
import GetAppIcon from '@material-ui/icons/GetApp';
import { useStyles } from './ProjectTabsStyles';
import {
  fetchTabConfigurationAPI,
  getTabAPI,
  getTabResponseKey
} from "./tabDataAPI";
import { getTabMapper } from "./tabDataMapper";

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`project-tabpanel-${index}`}
      aria-labelledby={`project-tab-${index}`}
      style={{ width: '100%', maxWidth: '100%', overflowX: 'hidden', boxSizing: 'border-box' }}
      {...other}
    >
      {value === index && (
        <Box style={{ padding: '0 0 0 12px', width: '100%', maxWidth: '100%', overflowX: 'hidden', boxSizing: 'border-box' }}>
          {children}
        </Box>
      )}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.any.isRequired,
  value: PropTypes.any.isRequired,
};

function a11yProps(index) {
  return {
    id: `project-tab-${index}`,
    "aria-controls": `project-tabpanel-${index}`,
  };
}


export default function ProjectTabs({
  data,
  handleRefresh,
  openedProjectTab,
  handleOpenProjectTab,
  projectColumnConfig = null, // Column configuration from project data
  featuresAccess = {}, // Feature access object for filtering actions
  showActionsColumn = false, // Whether to show actions column in tables
  onActionClick = null, // Optional callback when action is clicked
  batchId = null, // Batch ID for batch-level tabs (required)
  collabrixCampaignID: campaignIdProp = null, // Direct campaign ID prop (from mfg collab row)
  operation = 'All', // Operation filter (default: "All")
  viewType: externalViewType = null, // View type from parent (FilterBar)
  showChatBox: externalShowChatBox = null, // Chat box state from parent
  showMessages: externalShowMessages = null, // Messages state from parent
  onViewTypeChange: externalOnViewTypeChange = null, // View type change handler from parent
  onMessagesClick: externalOnMessagesClick = null, // Messages click handler from parent
  onChatClick: externalOnChatClick = null, // Chat click handler from parent
  onBackToTabs: externalOnBackToTabs = null, // Back to tabs handler from parent
  rowData = null, // Full row data from MfgMainTable for Comments etc.
  pageSize: externalPageSize = null, // Per-page controlled by parent (rendered beside "Batch Analytics")
}) {
  const classes = useStyles();
  // Use external state if provided, otherwise use internal state
  const [internalViewType, setInternalViewType] = useState("systemView");
  const [internalShowChatBox, setInternalShowChatBox] = useState(false);
  const [internalShowMessages, setInternalShowMessages] = useState(false);
  
  const viewType = externalViewType !== null ? externalViewType : internalViewType;
  const showChatBox = externalShowChatBox !== null ? externalShowChatBox : internalShowChatBox;
  const showMessages = externalShowMessages !== null ? externalShowMessages : internalShowMessages;
  
  // Store numeric view parameter (1 for overall, 2 for system)
  const [viewParam, setViewParam] = useState(1);
  const [value, setValue] = useState(openedProjectTab ? openedProjectTab : 0);
  // Per-page for the current tab's table (default 10). When the parent supplies
  // `pageSize` (Manufacturing Collaboration renders the selector beside the
  // "Batch Analytics" button), this becomes controlled; otherwise it falls back
  // to the internal selector rendered in the tabs bar.
  const [internalPageSize, setInternalPageSize] = useState(10);
  const pageSize = externalPageSize != null ? externalPageSize : internalPageSize;
  const setPageSize = setInternalPageSize;
  const [staticTabs, setStaticTabs] = useState([]); // Static tabs from fetchTabConfiguration
  const [tabDataMap, setTabDataMap] = useState({}); // Store data for each tab: { tabName: { columns, rows } }
  const [tabLoadingMap, setTabLoadingMap] = useState({}); // Store loading state for each tab: { tabName: boolean }
  const [loadingTabs, setLoadingTabs] = useState(false); // Loading state for fetching tab configuration
  const tabDataMapRef = useRef({}); // Ref to track current tab data for cache checking
  const fetchTabDataRef = useRef(null); // Ref to store fetchTabData function
  const hasAttemptedInitialFetch = useRef(false); // Flag to prevent repeated fetch attempts

  const allTabLabels = [
    "Materials & Consumption",
    "In-Process Tests",
    "Product Release Tests",
    "Deviations",
    "CC",
    "Cycle Time",
    "Equipment",
    "Yield",
    "Shipment",
    "Documents",
    "Comments",
  ];

  // Get collabrixCampaignID and collabrixBatchID from data - extract as separate values
  // Simplified extraction: use batchId directly, find campaign ID with multiple fallbacks
  const collabrixCampaignID = useMemo(() => {
    // If direct prop provided, use it first
    if (campaignIdProp) return campaignIdProp;
    console.log('[ProjectTabs] Extracting collabrixCampaignID', {
      hasData: !!data,
      hasProjectDetails: !!data?.projectDetails,
      hasCampaigns: !!data?.projectDetails?.campaigns,
      campaignsCount: data?.projectDetails?.campaigns?.length,
      batchId,
      fullData: data
    });
    
    // If no data, return null
    if (!data) {
      console.warn('[ProjectTabs] No data provided for collabrixCampaignID extraction');
      return null;
    }
    
    // Try to find from projectDetails.campaigns structure
    if (data.projectDetails?.campaigns && Array.isArray(data.projectDetails.campaigns) && data.projectDetails.campaigns.length > 0) {
      // If batchId is provided, try to find the campaign that contains it
      if (batchId) {
        for (const campaign of data.projectDetails.campaigns) {
          for (const stage of campaign.stages || []) {
            for (const batch of stage.batches || []) {
              if (batch.batchId === batchId || batch.batchID === batchId || 
                  batch.batchId?.toString() === batchId?.toString() || 
                  batch.batchID?.toString() === batchId?.toString()) {
                const foundID = campaign.collabrixCampaignID || campaign.campaignID || campaign.campaignId;
                console.log('[ProjectTabs] Found matching batch, returning campaignID:', foundID);
                return foundID;
              }
            }
          }
        }
      }
      
      // Fallback: use first campaign (always available if campaigns exist)
      const firstCampaign = data.projectDetails.campaigns[0];
      const fallbackID = firstCampaign?.collabrixCampaignID || firstCampaign?.campaignID || firstCampaign?.campaignId;
      console.log('[ProjectTabs] Using first campaign as fallback:', fallbackID);
      return fallbackID;
    }
    
    // Additional fallback: check if data has campaignId directly
    if (data.campaignId) {
      console.log('[ProjectTabs] Using data.campaignId:', data.campaignId);
      return data.campaignId;
    }
    
    // Last fallback: check campaignID
    if (data.campaignID) {
      console.log('[ProjectTabs] Using data.campaignID:', data.campaignID);
      return data.campaignID;
    }
    
    console.warn('[ProjectTabs] Could not extract collabrixCampaignID, returning null');
    return null;
  }, [data, batchId, campaignIdProp]);

  const collabrixBatchID = useMemo(() => {
    console.log('[ProjectTabs] Extracting collabrixBatchID', {
      hasData: !!data,
      batchId
    });
    
    // If batchId is provided as prop, use it directly (simplest approach)
    if (batchId) {
      // Try to find collabrixBatchID from data structure first
      if (data?.projectDetails?.campaigns && Array.isArray(data.projectDetails.campaigns)) {
        for (const campaign of data.projectDetails.campaigns) {
          for (const stage of campaign.stages || []) {
            for (const batch of stage.batches || []) {
              if (batch.batchId === batchId || batch.batchID === batchId || 
                  batch.batchId?.toString() === batchId?.toString() || 
                  batch.batchID?.toString() === batchId?.toString()) {
                const foundID = batch.collabrixBatchID || batch.batchId || batch.batchID;
                console.log('[ProjectTabs] Found matching batch in data, returning:', foundID);
                return foundID;
              }
            }
          }
        }
      }
      
      // Use batchId directly as it's provided as prop
      console.log('[ProjectTabs] Using batchId prop directly as collabrixBatchID:', batchId);
      return batchId;
    }
    
    // If no batchId prop, try to extract from data
    if (data?.projectDetails?.campaigns && Array.isArray(data.projectDetails.campaigns)) {
      const firstCampaign = data.projectDetails.campaigns[0];
      const firstStage = firstCampaign?.stages?.[0];
      const firstBatch = firstStage?.batches?.[0];
      if (firstBatch) {
        const fallbackID = firstBatch.collabrixBatchID || firstBatch.batchId || firstBatch.batchID;
        console.log('[ProjectTabs] Using first batch from data:', fallbackID);
        return fallbackID;
      }
    }
    
    console.warn('[ProjectTabs] No batchId provided and could not extract from data, returning null');
    return null;
  }, [data, batchId]);

  // Fetch static tab configuration when component mounts or viewType/campaignID changes
  // This is called when chevron is clicked (component mounts)
  useEffect(() => {
    // Clear any existing tabs first to avoid showing static data
    setStaticTabs([]);
    setTabDataMap({});
    setTabLoadingMap({});
    tabDataMapRef.current = {};
    
    const fetchTabConfig = async () => {
      try {
        // Use static campaign ID if not available (for testing)
        const campaignID = collabrixCampaignID || "CAMP-001";
        
        console.log('[ProjectTabs] Fetching tab configuration...', {
          viewType,
          campaignID,
          usingStatic: !collabrixCampaignID
        });
        setLoadingTabs(true);
        
        // Convert viewType to numeric parameter (1 for overall, 2 for system)
        const numericViewParam = getViewTypeParam(viewType);
        setViewParam(numericViewParam);
        
        // Fetch tab configuration - ALWAYS call API
        const response = await fetchTabConfigurationAPI(numericViewParam, campaignID);
        
        console.log('[ProjectTabs] Tab configuration response:', response);
        
        // Handle response structure: response.data.tabConfigurationList or response.data.data.tabConfigurationList
        const tabConfigurationList = response?.data?.tabConfigurationList || response?.data?.data?.tabConfigurationList;
        
        if (response?.status && tabConfigurationList && Array.isArray(tabConfigurationList) && tabConfigurationList.length > 0) {
          // Extract tab names from response — only use tabs returned by the real API
          const tabNames = tabConfigurationList.map(tab => tab.tabName);

          // ===== STATIC TABS (commented out — using only API-driven tabs) =====
          // const staticOnlyTabs = ["Equipment", "Yield"];
          // const missingStaticTabs = staticOnlyTabs.filter(name => !tabNames.includes(name));
          // const allTabs = [...tabNames, ...missingStaticTabs];

          // CPPs tab is rendered from static data (see CPPsTab); inject it if the
          // API config does not already include it, positioned after
          // "Materials & Consumption" to match the design.
          if (!tabNames.includes('CPPs')) {
            const mcIndex = tabNames.indexOf('Materials & Consumption');
            if (mcIndex >= 0) {
              tabNames.splice(mcIndex + 1, 0, 'CPPs');
            } else {
              tabNames.push('CPPs');
            }
          }

          console.log('[ProjectTabs] Setting tabs from API:', tabNames);
          setStaticTabs(tabNames);

          // Note: First tab will be fetched in a separate useEffect when staticTabs changes
        } else {
          console.warn('[ProjectTabs] No tabs in response');
          setStaticTabs([]);
        }
      } catch (error) {
        console.error("[ProjectTabs] Error fetching tab configuration:", error);
        setStaticTabs([]);
      } finally {
        setLoadingTabs(false);
      }
    };

    fetchTabConfig();
  }, [viewType, collabrixCampaignID, collabrixBatchID]);

  // Get visible tabs - ONLY use API data, no fallback
  const visibleTabs = useMemo(() => {
    // Only return tabs from API - no static fallback
    return staticTabs && staticTabs.length > 0 ? staticTabs : [];
  }, [staticTabs]);

  // Fetch first tab (or restored tab) data immediately after tabs are loaded from API
  useEffect(() => {
    // Only run if we have tabs from API
    if (staticTabs.length > 0) {

      // Check if there's a saved tab to restore (from navigating back)
      const savedTab = sessionStorage.getItem('mfg_active_tab');
      const savedTabIndex = savedTab ? staticTabs.indexOf(savedTab) : -1;

      const targetTabName = savedTabIndex >= 0 ? savedTab : staticTabs[0];
      const targetTabIndex = savedTabIndex >= 0 ? savedTabIndex : visibleTabs.indexOf(staticTabs[0]);

      // Clear saved tab after restoring
      if (savedTab) {
        sessionStorage.removeItem('mfg_active_tab');
      }

      console.log('[ProjectTabs] Auto-fetching tab data:', targetTabName, {
        restored: !!savedTab,
        hasCampaignID: !!collabrixCampaignID,
        hasBatchID: !!collabrixBatchID,
        willUseStatic: !collabrixCampaignID || !collabrixBatchID,
        fetchTabDataRefReady: !!fetchTabDataRef.current
      });

      // Set loading state immediately
      setTabLoadingMap(prev => ({ ...prev, [targetTabName]: true }));

      // Clear any existing data
      setTabDataMap(prev => {
        const updated = { ...prev, [targetTabName]: { columns: [], rows: [] } };
        tabDataMapRef.current = updated;
        return updated;
      });

      // Set the target tab as active
      if (targetTabIndex >= 0) {
        setValue(targetTabIndex);
        if (handleOpenProjectTab) {
          handleOpenProjectTab(targetTabIndex);
        }
      }

      // Fetch data for target tab
      if (fetchTabDataRef.current) {
        fetchTabDataRef.current(targetTabName, true);
      } else {
        const timeoutId = setTimeout(() => {
          if (fetchTabDataRef.current) {
            console.log('[ProjectTabs] fetchTabDataRef now ready, calling tab API');
            fetchTabDataRef.current(targetTabName, true);
          }
        }, 100);
        return () => clearTimeout(timeoutId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staticTabs, visibleTabs]);

  // Get the visible tab index from the original tab index
  const getVisibleTabIndex = (originalIndex) => {
    // Use visibleTabs directly since we only use API data
    if (originalIndex >= 0 && originalIndex < visibleTabs.length) {
      return originalIndex;
    }
    return 0;
  };

  // Fetch data for a specific tab
  const fetchTabData = useCallback(async (tabName, forceRefresh = false) => {
    
    // Use real IDs from component props for tab API calls
    const batchID = collabrixBatchID || "";
    
    console.log(`[ProjectTabs] fetchTabData called for tab: ${tabName}`, {
      collabrixCampaignID,
      batchID,
      forceRefresh,
    });
    
    // Comments tab — uses PO Dashboard chat APIs (separate component handles it)
    if (tabName === "Comments") {
      setTabLoadingMap(prev => ({ ...prev, [tabName]: false }));
      setTabDataMap(prev => {
        const updated = { ...prev, [tabName]: { columns: [], rows: [], isStatic: true } };
        tabDataMapRef.current = updated;
        return updated;
      });
      return;
    }

    // CPPs tab — rendered entirely from static data (see CPPsTab), no API call.
    if (tabName === "CPPs") {
      setTabLoadingMap(prev => ({ ...prev, [tabName]: false }));
      setTabDataMap(prev => {
        const updated = { ...prev, [tabName]: { columns: [], rows: [], isStatic: true } };
        tabDataMapRef.current = updated;
        return updated;
      });
      return;
    }

    // Special handling for Batch Records and Quality Events - no API call, just show "No data" after 1 second loader
    if (tabName === "Batch Records" || tabName === "Quality Events") {
      console.log(`[ProjectTabs] Special handling for tab: ${tabName} - showing loader then "No data"`);
      
      // Set loading state for this tab
      setTabLoadingMap(prev => ({ ...prev, [tabName]: true }));
      
      // Clear old data immediately
      setTabDataMap(prev => {
        const updated = { ...prev, [tabName]: { columns: [], rows: [] } };
        tabDataMapRef.current = updated;
        return updated;
      });
      
      // Wait 1 second (loader), then show "No data"
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Set "No data" message
      const newData = { columns: [], rows: [], message: "No data found for batch number." };
      setTabDataMap(prev => {
        const updated = { ...prev, [tabName]: newData };
        tabDataMapRef.current = updated;
        return updated;
      });
      
      // Clear loading state
      setTabLoadingMap(prev => ({ ...prev, [tabName]: false }));
      return;
    }
    
    // Check if data already exists (unless force refresh) - use ref to check current state
    if (!forceRefresh && tabDataMapRef.current[tabName] && 
        tabDataMapRef.current[tabName].rows && 
        tabDataMapRef.current[tabName].rows.length > 0) {
      console.log(`[ProjectTabs] Data already exists for tab: ${tabName}, skipping fetch (use forceRefresh=true to bypass)`);
      return; // Data already loaded, skip API call
    }
    
    console.log(`[ProjectTabs] Proceeding with API call for tab: ${tabName} (forceRefresh=${forceRefresh})`);
    
    // Get API function for this tab (will be created dynamically if not in explicit map)
    const apiFunction = getTabAPI(tabName);
    if (!apiFunction) {
      console.warn(`[ProjectTabs] No API function found for tab: ${tabName}`);
      return;
    }
    
    console.log(`[ProjectTabs] API function found/created for tab: ${tabName}`);
    
    try {
      // Set loading state for this tab
      setTabLoadingMap(prev => ({ ...prev, [tabName]: true }));
      
      // Clear old data immediately to avoid showing stale data
      setTabDataMap(prev => {
        const updated = { ...prev, [tabName]: { columns: [], rows: [] } };
        tabDataMapRef.current = updated;
        return updated;
      });
      
      const payload = {
        viewTab: "",
        collabrixBatchID: batchID || "",
      };
      
      console.log(`[ProjectTabs] ===== CALLING API FOR TAB: ${tabName} =====`);
      console.log(`[ProjectTabs] Payload:`, JSON.stringify(payload, null, 2));
      
      // Call API
      const response = await apiFunction(payload);
      
      console.log(`[ProjectTabs] API response for tab: ${tabName}`, response);
      console.log(`[ProjectTabs] Response structure:`, {
        hasStatus: !!response?.status,
        hasData: !!response?.data,
        hasDirectList: !!response?.cppDetailsList || !!response?.materialAndConsumptionList || !!response?.inProcessTestList,
        responseKeys: Object.keys(response || {})
      });
      
      // Extract raw data by response key for custom tab components
      const responseKey = getTabResponseKey(tabName);
      const rawData = responseKey
        ? (response?.[responseKey] || response?.data?.[responseKey] || null)
        : null;

      // Map response to table format
      const mapper = getTabMapper(tabName);
      if (mapper) {
        // Pass the full response object to mapper (it will handle different structures)
        const { columns, rows } = mapper(response);
        console.log(`[ProjectTabs] Mapped data for tab: ${tabName}`, {
          columnsCount: columns.length,
          rowsCount: rows.length,
          sampleRow: rows[0]
        });
        const newData = { columns, rows, rawData };
        setTabDataMap(prev => {
          const updated = { ...prev, [tabName]: newData };
          tabDataMapRef.current = updated; // Update ref
          return updated;
        });
      } else if (rawData) {
        // Fallback: use raw extracted data directly
        console.log(`[ProjectTabs] Using raw data for tab: ${tabName}`);
        const rows = Array.isArray(rawData) ? rawData : [];
        const newData = { columns: [], rows, rawData };
        setTabDataMap(prev => {
          const updated = { ...prev, [tabName]: newData };
          tabDataMapRef.current = updated; // Update ref
          return updated;
        });
      } else {
        console.warn(`[ProjectTabs] No mapper and no response key found for tab: ${tabName}`);
        const newData = { columns: [], rows: [] };
        setTabDataMap(prev => {
          const updated = { ...prev, [tabName]: newData };
          tabDataMapRef.current = updated; // Update ref
          return updated;
        });
      }
    } catch (error) {
      console.error(`[ProjectTabs] Error fetching data for tab ${tabName}:`, error);
      // Simplified error message
      const errorMessage = "Failed to load";
      const newData = { columns: [], rows: [], error: errorMessage };
      setTabDataMap(prev => {
        const updated = { ...prev, [tabName]: newData };
        tabDataMapRef.current = updated; // Update ref
        return updated;
      });
    } finally {
      setTabLoadingMap(prev => ({ ...prev, [tabName]: false }));
    }
  }, [collabrixCampaignID, collabrixBatchID, viewType]);
  
  // Store fetchTabData in ref so it can be used in useEffect without causing dependency issues
  useEffect(() => {
    fetchTabDataRef.current = fetchTabData;
  }, [fetchTabData]);

  const handleChange = async (event, newValue) => {
    console.log(`[ProjectTabs] ===== TAB CLICKED =====`);
    console.log(`[ProjectTabs] Tab changed to index: ${newValue}`, {
      visibleTabs,
      newValue,
      visibleTabsLength: visibleTabs.length
    });
    
    // newValue is the index in visibleTabs (use it directly since tabs come from API)
    const tabName = visibleTabs[newValue];
    console.log(`[ProjectTabs] Selected tab name: "${tabName}"`);
    
    if (!tabName) {
      console.error(`[ProjectTabs] ERROR: No tab name found for index: ${newValue}`);
      console.error(`[ProjectTabs] visibleTabs:`, visibleTabs);
      return;
    }
    
    // Use the visibleTabs index directly since tabs are dynamic from API
    setValue(newValue);

    // Save active tab name for restore on back navigation
    sessionStorage.setItem('mfg_active_tab', tabName);

    // Hide messages view when switching to a regular tab
    if (showMessages) {
      if (externalOnMessagesClick) {
        externalOnMessagesClick();
      } else {
        setInternalShowMessages(false);
      }
    }

    if (handleOpenProjectTab) {
      handleOpenProjectTab(newValue);
    }
    
    // Always fetch data for the selected tab when user clicks (force refresh to ensure API is called)
    console.log(`[ProjectTabs] Calling fetchTabData for tab: "${tabName}" (forceRefresh=true)`);
    try {
      await fetchTabData(tabName, true); // true = force refresh, always call API on user click
    } catch (error) {
      console.error(`[ProjectTabs] Error in handleChange for tab ${tabName}:`, error);
    }
    console.log(`[ProjectTabs] ===== TAB CLICK HANDLED =====`);
  };

  // Sync with parent component's openedProjectTab and fetch data
  useEffect(() => {
    // Only run if we have all required data
    if (openedProjectTab === undefined || openedProjectTab === null) {
      return;
    }
    
    if (!collabrixBatchID || !fetchTabDataRef.current) {
      // Don't log warning repeatedly - only once
      if (!hasAttemptedInitialFetch.current) {
        console.warn("[ProjectTabs] Missing batch ID, cannot fetch tab data", {
          collabrixBatchID
        });
        hasAttemptedInitialFetch.current = true;
      }
      return;
    }
    
    // Reset flag when we have valid IDs
    hasAttemptedInitialFetch.current = false;
    
    // Check if the opened tab index is valid for visible tabs
    if (openedProjectTab >= 0 && openedProjectTab < visibleTabs.length) {
      const label = visibleTabs[openedProjectTab];
      setValue(openedProjectTab);
      // Fetch data for this tab
      fetchTabDataRef.current(label, false);
    } else if (visibleTabs.length > 0) {
      // If current tab is not valid, switch to first visible tab
      const firstVisibleTab = visibleTabs[0];
      setValue(0);
      if (handleOpenProjectTab) {
        handleOpenProjectTab(0);
      }
      // Fetch data for first visible tab
      if (firstVisibleTab) {
        fetchTabDataRef.current(firstVisibleTab, false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openedProjectTab, collabrixCampaignID, collabrixBatchID]);

  // Reset to first visible tab when view type changes and fetch data
  useEffect(() => {
    // Only run if we have all required data and tabs are available
    if (visibleTabs.length === 0 || !collabrixBatchID || !fetchTabDataRef.current) {
      return;
    }

    const firstVisibleTab = visibleTabs[0];
    setValue(0);
    if (handleOpenProjectTab) {
      handleOpenProjectTab(0);
    }
    // Clear tab data when view type changes
    setTabDataMap({});
    tabDataMapRef.current = {}; // Clear ref
    // Fetch data for first visible tab
    fetchTabDataRef.current(firstVisibleTab, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewType, collabrixCampaignID, collabrixBatchID]);

  // Clear and refetch tab data when batchId or operation changes
  useEffect(() => {
    // Only run if we have all required data
    if (visibleTabs.length === 0 || value === null || value === undefined || !collabrixBatchID || !fetchTabDataRef.current) {
      return;
    }
    
    // Use visibleTabs directly since we only use API data
    if (value >= 0 && value < visibleTabs.length) {
      const currentTabName = visibleTabs[value];
      // Clear existing data and refetch
      setTabDataMap(prev => {
        const newMap = { ...prev };
        delete newMap[currentTabName];
        tabDataMapRef.current = newMap; // Update ref
        return newMap;
      });
      fetchTabDataRef.current(currentTabName, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchId, operation, collabrixCampaignID, collabrixBatchID]);

  const handleMessagesClick = () => {
    if (externalOnMessagesClick) {
      externalOnMessagesClick();
    } else {
      setInternalShowMessages(!showMessages);
      setInternalShowChatBox(false);
    }
  };

  const handleViewTypeChange = async (newViewType) => {
    if (externalOnViewTypeChange) {
      externalOnViewTypeChange(newViewType);
    } else {
      setInternalViewType(newViewType);
    }
    // Close messages view when switching to System/Overall View
    if (showMessages) {
      if (externalOnMessagesClick) {
        externalOnMessagesClick();
      } else {
        setInternalShowMessages(false);
      }
    }
    // Convert to numeric parameter and update
    const numericViewParam = getViewTypeParam(newViewType);
    setViewParam(numericViewParam);
    // API call will be triggered by useEffect when viewType changes
  };

  const handleChatClick = () => {
    if (externalOnChatClick) {
      externalOnChatClick();
    } else {
      setInternalShowChatBox(!showChatBox);
    }
  };

  // Download the main summary table + the currently-selected tab's table data as
  // an Excel workbook named "<batchNumber>_<Tab Name>.xlsx".
  const activeTabName = (value >= 0 && value < visibleTabs.length) ? visibleTabs[value] : null;
  const handleDownloadExcel = () => {
    if (!activeTabName) return;
    try {
      downloadTabExcel({
        tabName: activeTabName,
        rowData,
        tabData: tabDataMap[activeTabName] || { columns: [], rows: [] },
      });
    } catch (error) {
      console.error('[ProjectTabs] Excel download failed:', error);
    }
  };

  return (
    <div className={classes.root} style={{ width: '100%', overflowX: 'hidden', boxSizing: 'border-box' }}>
      <AppBar position="static" className={classes.tabs} elevation={0}>
        <Box className={classes.tabsWithActions}>
          {loadingTabs ? (
              // Show smooth progress bar loading for tabs
              <Box style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', padding: '0 16px', height: '48px', position: 'relative' }}>
                <Box
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    height: '2px',
                    width: '100%',
                    backgroundColor: '#e0e0e0',
                  }}
                />
                <Box
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    height: '2px',
                    width: '30%',
                    backgroundColor: '#1976d2',
                    borderRadius: '0 2px 2px 0',
                    animation: 'progressMove 1.5s ease-in-out infinite',
                  }}
                />
                <Box style={{ display: 'flex', alignItems: 'center', gap: '16px', marginLeft: 'auto', marginRight: 'auto' }}>
                  <CircularProgress size={20} thickness={4} style={{ color: '#1976d2' }} />
                  <Typography variant="body2" style={{ color: '#666', fontSize: '14px' }}>
                    Loading tabs...
                  </Typography>
                </Box>
                <style>
                  {`
                    @keyframes progressMove {
                      0% {
                        left: 0;
                        width: 30%;
                      }
                      50% {
                        left: 35%;
                        width: 30%;
                      }
                      100% {
                        left: 70%;
                        width: 30%;
                      }
                    }
                  `}
                </style>
              </Box>
            ) : (
              <Tabs
                value={showMessages ? false : (value >= 0 && value < visibleTabs.length ? value : 0)}
                onChange={handleChange}
                aria-label="project tabs"
                classes={{ indicator: classes.indicator, root: classes.tabsRoot }}
                variant="scrollable"
                scrollButtons="auto"
                style={{ flex: 1, minWidth: 0 }}
              >
                {visibleTabs.map((tabName, visibleIndex) => {
                  return (
                    <Tab
                      key={tabName}
                      label={tabName}
                      {...a11yProps(visibleIndex)}
                      className={classes.tab}
                    />
                  );
                })}
              </Tabs>
            )
          }
          {/* Download — exports the main summary table + the selected tab's table
              data to Excel (<batchNumber>_<Tab Name>.xlsx). Sits at the right end
              of the tabs bar, beside the tabs. Hidden on the Documents and Comments
              tabs (no tabular report to export there). */}
          {!loadingTabs && !showMessages && activeTabName &&
            activeTabName !== 'Documents' && activeTabName !== 'Comments' && (
            <Box style={{ display: 'flex', alignItems: 'center', flexShrink: 0, paddingLeft: '8px', marginLeft: 'auto' }}>
              <Button
                variant="outlined"
                onClick={handleDownloadExcel}
                style={{
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                }}
              >
                Download Report
              </Button>
            </Box>
          )}
          {/* Per Page selector — controls the number of rows shown in the
              current tab's table (default 10). Sits at the upper-right of the
              tabs bar, matching the dashboard listing style. */}
          {/* PER PAGE selector commented out — Manufacturing tab tables show ALL rows
              (sticky header + scrollable). Pagination is disabled. */}
          {/* {!loadingTabs && externalPageSize == null && (
            <Box
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                flexShrink: 0,
                paddingLeft: '8px',
                marginLeft: 'auto',
                borderLeft: '1px solid #e0e0e0',
              }}
            >
              <Typography
                variant="body2"
                style={{ fontWeight: 700, whiteSpace: 'nowrap', color: '#333', fontSize: '0.75rem', letterSpacing: '0.03em' }}
              >
                PER PAGE
              </Typography>
              <FormControl variant="outlined" size="small">
                <Select
                  value={pageSize}
                  onChange={(e) => setPageSize(e.target.value)}
                  style={{ backgroundColor: '#fff' }}
                  MenuProps={{ getContentAnchorEl: null, anchorOrigin: { vertical: 'bottom', horizontal: 'left' } }}
                >
                  {[5, 10, 15, 20, 25, 30].map((n) => (
                    <MenuItem key={n} value={n}>{n}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )} */}
          {/* Action buttons on the right side - show only after tabs are loaded */}
          {/* {!loadingTabs && (
            <Box className={classes.actionButtonsContainer} style={{ flexShrink: 0, paddingLeft: '8px', borderLeft: '1px solid #e0e0e0' }}>
            <Button
              className={classes.messagesButton}
              onClick={handleMessagesClick}
            >
              Messages
            </Button>
            </Box>
          )} */}
        </Box>
      </AppBar>

      <Box style={{ marginTop: '10px', width: '100%', maxWidth: '100%', boxSizing: 'border-box', overflowX: 'hidden', flex: 1, minHeight: 0 }}>
        {showMessages ? (
          <Box style={{ width: '100%', flex: 1, minWidth: 0, paddingTop: '10px' }}>
            <MessagesDashboard />
          </Box>
        ) : (
          <Box style={{ width: '100%', maxWidth: '100%', overflowX: 'hidden', boxSizing: 'border-box' }}>
            {/* Render tabs from API response */}
            {visibleTabs.map((tabName, visibleIndex) => {
              const tabData = tabDataMap[tabName] || { columns: [], rows: [] };
              const isLoading = tabLoadingMap[tabName] || false;
              const hasError = tabData.error;
              
              // Get actions configuration for this tab
              const tabActions = showActionsColumn 
                ? getTabActionsConfig(tabName, { featuresAccess, onActionClick })
                : [];
              
              // Get appropriate message based on state - simplified messages
              // Don't show "no data" while loading
              const getEmptyMessage = () => {
                if (isLoading) {
                  return null; // Don't show message while loading
                }
                // Use custom message if provided (e.g., for Batch Records and Quality Events)
                if (tabData.message) {
                  return tabData.message;
                }
                if (hasError) {
                  return `Failed to load ${tabName} data.`;
                }
                if (tabData.rows && tabData.rows.length === 0) {
                  return `No data found for batch number.`;
                }
                if (!tabData.rows) {
                  return `No data found for batch number.`;
                }
                return `No data found for batch number.`;
              };
              
              // Custom tab components — wired to API data
              if (tabName === 'Planning') {
                return (
                  <TabPanel key={tabName} value={value} index={visibleIndex}>
                    <PlanningTab loading={isLoading} batchNumber={rowData?.batchNumber} pageSize={pageSize} />
                  </TabPanel>
                );
              }

              if (tabName === 'MFG Status') {
                return (
                  <TabPanel key={tabName} value={value} index={visibleIndex}>
                    <MFGStatusTab loading={isLoading} rowData={rowData} pageSize={pageSize} />
                  </TabPanel>
                );
              }

              if (tabName === 'Materials & Consumption') {
                return (
                  <TabPanel key={tabName} value={value} index={visibleIndex}>
                    <MaterialsConsumptionTab data={tabData.rows} loading={isLoading} batchNumber={rowData?.batchNumber} pageSize={pageSize} />
                  </TabPanel>
                );
              }

              if (tabName === 'In-Process Tests') {
                return (
                  <TabPanel key={tabName} value={value} index={visibleIndex}>
                    <InProcessTestsTab data={tabData.rows} loading={isLoading} batchNumber={rowData?.batchNumber} pageSize={pageSize} />
                  </TabPanel>
                );
              }

              if (tabName === 'Product Release Tests') {
                return (
                  <TabPanel key={tabName} value={value} index={visibleIndex}>
                    <ProductReleaseTestsTab data={tabData.rows} loading={isLoading} batchNumber={rowData?.batchNumber} pageSize={pageSize} />
                  </TabPanel>
                );
              }

              if (tabName === 'Deviations') {
                return (
                  <TabPanel key={tabName} value={value} index={visibleIndex}>
                    <DeviationsTab data={tabData.rows} loading={isLoading} rowData={rowData} pageSize={pageSize} />
                  </TabPanel>
                );
              }

              if (tabName === 'CC') {
                return (
                  <TabPanel key={tabName} value={value} index={visibleIndex}>
                    <ChangeControlTab data={tabData.rows} loading={isLoading} rowData={rowData} pageSize={pageSize} />
                  </TabPanel>
                );
              }

              if (tabName === 'Cycle Time') {
                return (
                  <TabPanel key={tabName} value={value} index={visibleIndex}>
                    <CycleTimeTab data={tabData.rows} loading={isLoading} pageSize={pageSize} />
                  </TabPanel>
                );
              }

              if (tabName === 'Equipment') {
                return (
                  <TabPanel key={tabName} value={value} index={visibleIndex}>
                    <EquipmentTab data={tabData.rows} loading={isLoading} batchNumber={rowData?.batchNumber} pageSize={pageSize} />
                  </TabPanel>
                );
              }

              if (tabName === 'Yield') {
                return (
                  <TabPanel key={tabName} value={value} index={visibleIndex}>
                    <YieldTab data={tabData.rows} loading={isLoading} rowData={rowData} pageSize={pageSize} />
                  </TabPanel>
                );
              }

              if (tabName === 'Shipment') {
                return (
                  <TabPanel key={tabName} value={value} index={visibleIndex}>
                    <ShipmentTab data={tabData.rows} loading={isLoading} />
                  </TabPanel>
                );
              }

              if (tabName === 'CPPs') {
                return (
                  <TabPanel key={tabName} value={value} index={visibleIndex}>
                    <CPPsTab loading={isLoading} batchNumber={rowData?.batchNumber} pageSize={pageSize} />
                  </TabPanel>
                );
              }

              if (tabName === 'Documents') {
                return (
                  <TabPanel key={tabName} value={value} index={visibleIndex}>
                    <DocumentsTab rowData={rowData} />
                  </TabPanel>
                );
              }

              if (tabName === 'Comments') {
                return (
                  <TabPanel key={tabName} value={value} index={visibleIndex}>
                    <CommentsTab batchId={batchId} rowData={rowData} />
                  </TabPanel>
                );
              }

              return (
                <TabPanel key={tabName} value={value} index={visibleIndex}>
                  {isLoading ? (
                    <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" style={{ padding: '40px', gap: '16px' }}>
                      <Box 
                        style={{
                          display: 'flex',
                          gap: '8px',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        {[0, 1, 2].map((i) => (
                          <Box
                            key={i}
                            style={{
                              width: '12px',
                              height: '12px',
                              borderRadius: '50%',
                              backgroundColor: '#1976d2',
                              animation: `pulse 1.4s ease-in-out ${i * 0.16}s infinite`,
                            }}
                          />
                        ))}
                      </Box>
                      <style>
                        {`
                          @keyframes pulse {
                            0%, 80%, 100% {
                              transform: scale(0.8);
                              opacity: 0.5;
                            }
                            40% {
                              transform: scale(1.2);
                              opacity: 1;
                            }
                          }
                        `}
                      </style>
                      <Typography variant="body2" style={{ color: '#666', marginTop: '8px' }}>
                        Loading {tabName} data...
                      </Typography>
                    </Box>
                  ) : tabData.rows && tabData.rows.length > 0 ? (
                    <GenericTable
                      data={tabData.rows}
                      columns={tabData.columns}
                      viewType={viewType}
                      pageSize={pageSize}
                      actions={tabActions}
                      featuresAccess={featuresAccess}
                      onActionClick={onActionClick}
                      showActionsColumn={showActionsColumn}
                    />
                  ) : getEmptyMessage() ? (
                    <Box style={{ padding: '40px', textAlign: 'center' }}>
                      <Typography variant="body2" style={{ color: hasError ? '#d32f2f' : '#666' }}>
                        {getEmptyMessage()}
                      </Typography>
                    </Box>
                  ) : null}
                </TabPanel>
              );
            })}
          </Box>
        )}
      </Box>
    </div>
  );
}

